import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Typography
} from '@mui/joy';
import { observer } from 'mobx-react-lite';

import ResponsiveLayout from '../components/layout/ResponsiveLayout';
import TableHeader from '../components/TableHeader';
import organizationDetailsSchema, { OrganizationDetailsForm } from '../schema/organizationDetailsSchema';
import { OrganizationService } from '../services/OrganizationService';
import organizationStore from '../stores/OrganizationStore';

const OrganizationSettings = observer(() => {
  const { t } = useTranslation();
  const { selectedOrg, organizations } = organizationStore;
  const [isEditing, setIsEditing] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find the current organization from the store
  const currentOrganization = organizations.find((org) => org.id === selectedOrg);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<OrganizationDetailsForm>({
    resolver: yupResolver(organizationDetailsSchema(t)),
    defaultValues: {
      name: currentOrganization?.name || ''
    }
  });

  useEffect(() => {
    if (currentOrganization) {
      reset({
        name: currentOrganization.name || ''
      });

      // Set logo preview if available
      if (currentOrganization.logo) {
        setLogoPreview(currentOrganization.logo);
      }
    }
  }, [currentOrganization, reset]);

  const handleEditDetails = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form values
    if (currentOrganization) {
      reset({
        name: currentOrganization.name || ''
      });

      // Reset logo preview
      if (currentOrganization.logo) {
        setLogoPreview(currentOrganization.logo);
      } else {
        setLogoPreview(null);
      }
    }
  };

  const handleLogoChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setValue('logo', file);

      // Create a preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit: SubmitHandler<OrganizationDetailsForm> = async (data) => {
    if (!selectedOrg) return;

    setIsSubmitting(true);
    try {
      await OrganizationService.updateOrganizationDetails(selectedOrg, data);

      // Update the organization in the store
      const updatedOrg = await OrganizationService.getOrganizationDetails(selectedOrg);
      const updatedOrgs = organizations.map((org) => (org.id === selectedOrg ? updatedOrg : org));
      organizationStore.setOrganizations(updatedOrgs);

      toast.success(t('organization.details_updated'));
      setIsEditing(false);
    } catch (error) {
      toast.error(t('organization.errors.update_failed'));
      console.error('Error updating organization details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveLayout>
      <TableHeader title={t('navigation.organization_settings')} />

      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Organization Details Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography level='title-lg' sx={{ mb: 2, flexGrow: 1 }}>
                {t('organization.details')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 0, ml: 2 }}>
                {!isEditing ? (
                  <Button color='neutral' onClick={handleEditDetails} size='md' variant='plain'>
                    {t('organization.edit_organization_details')}
                  </Button>
                ) : (
                  <>
                    <Button
                      color='neutral'
                      disabled={isSubmitting}
                      onClick={handleCancelEdit}
                      size='md'
                      variant='plain'
                    >
                      {t('edit_profile.cancel')}
                    </Button>
                    <Button
                      color='primary'
                      disabled={isSubmitting}
                      onClick={handleSubmit(onSubmit)}
                      size='md'
                      variant='solid'
                    >
                      {isSubmitting ? t('edit_profile.saving') : t('edit_profile.save_changes')}
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {!isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Logo */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography level='body-sm' sx={{ fontWeight: 'bold' }}>
                    {t('organization.logo')}:
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {logoPreview || currentOrganization?.logo ? (
                      <img
                        alt='Organization Logo'
                        src={logoPreview || currentOrganization?.logo}
                        style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '200px',
                          height: '100px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px dashed',
                          borderColor: 'neutral.300',
                          borderRadius: '4px'
                        }}
                      >
                        <Typography level='body-sm' sx={{ color: 'neutral.500' }}>
                          No logo uploaded
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Organization Name */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level='body-sm' sx={{ fontWeight: 'bold' }}>
                    {t('organization.org_name')}:
                  </Typography>
                  <Typography level='body-sm'>{currentOrganization?.name || 'Organization Name'}</Typography>
                </Box>
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* Logo Upload */}
                  <FormControl error={!!errors.logo}>
                    <FormLabel>{t('organization.logo')}</FormLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {logoPreview && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                          <img
                            alt='Organization Logo Preview'
                            src={logoPreview}
                            style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                          />
                        </Box>
                      )}
                      <Button color='neutral' component='label' variant='outlined'>
                        {t('organization.upload_logo')}
                        <input
                          accept='image/jpeg,image/png,image/svg+xml'
                          hidden
                          onChange={handleLogoChange}
                          type='file'
                        />
                      </Button>
                      <FormHelperText>{t('organization.logo_description')}</FormHelperText>
                      {errors.logo && <FormHelperText>{errors.logo.message}</FormHelperText>}
                    </Box>
                  </FormControl>

                  {/* Organization Name */}
                  <FormControl error={!!errors.name}>
                    <FormLabel>{t('organization.org_name')}</FormLabel>
                    <Controller
                      control={control}
                      name='name'
                      render={({ field }) => <Input {...field} placeholder={t('organization.org_name')} />}
                    />
                    {errors.name && <FormHelperText>{errors.name.message}</FormHelperText>}
                  </FormControl>
                </Box>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Organization Settings Card */}
      </Box>
    </ResponsiveLayout>
  );
});

export default OrganizationSettings;
